import React, { useState, useCallback, useRef } from "react";
import { Marker, Circle, Popup, useMapEvents, useMap } from "react-leaflet";
import { Map } from "@/components/ui/map";
import { Trash2, Plus, Target, Truck, MapPin, Package } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import L, { LatLngTuple } from "leaflet";

// Default Leaflet icon setup
import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";
import iconRetina from "leaflet/dist/images/marker-icon-2x.png";
import { clsx } from "clsx";
import { useField } from "formik";
import { BasemapLayer } from "@/components/map/BasemapLayer";

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconRetinaUrl: iconRetina,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

// Custom icons for departure, delivery, and both
const DepartureIcon = L.divIcon({
  html: `<div style="background-color: #059669; color: white; border-radius: 50%; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.2);"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg></div>`,
  className: "custom-marker",
  iconSize: [30, 30],
  iconAnchor: [15, 30],
  popupAnchor: [0, -30],
});

const DeliveryIcon = L.divIcon({
  html: `<div style="background-color: #2563eb; color: white; border-radius: 50%; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.2);"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18H9"/><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"/><circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/></svg></div>`,
  className: "custom-marker",
  iconSize: [30, 30],
  iconAnchor: [15, 30],
  popupAnchor: [0, -30],
});

const BothIcon = L.divIcon({
  html: `<div style="background: linear-gradient(45deg, #059669 0%, #059669 50%, #2563eb 50%, #2563eb 100%); color: white; border-radius: 50%; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.2);"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27,6.96 12,12.01 20.73,6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg></div>`,
  className: "custom-marker",
  iconSize: [30, 30],
  iconAnchor: [15, 30],
  popupAnchor: [0, -30],
});

L.Marker.prototype.options.icon = DefaultIcon;

// Types
type LocationType = "departure" | "delivery" | "both";

type Point = {
  lat: number;
  lng: number;
  radius: number;
  type: LocationType;
};

type Points = Point[];

interface LocationRadiusSelectorProps {
  value?: Points;
  onChange?: (points: Points) => void;
  defaultCenter?: LatLngTuple;
  defaultZoom?: number;
  height?: string;
  defaultRadius?: number;
  className?: string;
}

interface MapClickHandlerProps {
  onMapClick: (latlng: LatLngTuple) => void;
  isAddingMode: boolean;
}

interface PointMarkerProps {
  point: Point;
  index: number;
  onUpdateRadius: (index: number, radius: number) => void;
  onDelete: (index: number) => void;
  onTypeChange: (index: number, type: LocationType) => void;
}

// Map click handler component
function MapClickHandler({ onMapClick, isAddingMode }: MapClickHandlerProps) {
  useMapEvents({
    click: (e) => {
      if (isAddingMode) {
        onMapClick([e.latlng.lat, e.latlng.lng]);
      }
    },
  });
  return null;
}

// Individual point marker with popup controls
function PointMarker({
  point,
  index,
  onUpdateRadius,
  onDelete,
  onTypeChange,
}: PointMarkerProps) {
  const markerRef = useRef<L.Marker>(null);

  // Auto-open popup when marker is created
  React.useEffect(() => {
    if (markerRef.current) {
      markerRef.current.openPopup();
    }
  }, []);

  const handleRadiusChange = (value: number[]) => {
    onUpdateRadius(index, value[0]);
  };

  const handleTypeChange = (newType: LocationType) => {
    onTypeChange(index, newType);
  };

  const getIcon = () => {
    const type = point.type || "departure";
    if (type === "departure") return DepartureIcon;
    if (type === "delivery") return DeliveryIcon;
    return BothIcon;
  };

  const getCircleColor = () => {
    const type = point.type || "departure";
    if (type === "departure") return { fillColor: "#059669", color: "#047857" };
    if (type === "delivery") return { fillColor: "#2563eb", color: "#1d4ed8" };
    return { fillColor: "#7c3aed", color: "#5b21b6" }; // Purple for both
  };

  const getTypeLabel = () => {
    const type = point.type || "departure";
    if (type === "departure") return "Departure";
    if (type === "delivery") return "Delivery";
    return "Both";
  };

  return (
    <>
      <Marker
        ref={markerRef}
        position={[point.lat, point.lng]}
        icon={getIcon()}
      >
        <Popup>
          <div className="min-w-[220px] p-2">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">
                {getTypeLabel()} Point {index + 1}
              </h3>
              <button
                type="button"
                onClick={() => onDelete(index)}
                className="rounded-full p-1.5 text-red-500 transition-colors hover:bg-red-50 hover:text-red-700"
                title="Delete point"
              >
                <Trash2 size={16} />
              </button>
            </div>

            <div className="space-y-3">
              {/* Location Type Selector */}
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Location Type
                </label>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => handleTypeChange("departure")}
                    className={`flex flex-1 items-center justify-center gap-1 rounded-lg px-2 py-2 text-xs font-medium transition-colors ${
                      (point.type || "departure") === "departure"
                        ? "bg-green-100 text-green-800 ring-2 ring-green-500"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    <MapPin size={12} />
                    Departure
                  </button>
                  <button
                    type="button"
                    onClick={() => handleTypeChange("delivery")}
                    className={`flex flex-1 items-center justify-center gap-1 rounded-lg px-2 py-2 text-xs font-medium transition-colors ${
                      (point.type || "departure") === "delivery"
                        ? "bg-blue-100 text-blue-800 ring-2 ring-blue-500"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    <Truck size={12} />
                    Delivery
                  </button>
                  <button
                    type="button"
                    onClick={() => handleTypeChange("both")}
                    className={`flex flex-1 items-center justify-center gap-1 rounded-lg px-2 py-2 text-xs font-medium transition-colors ${
                      (point.type || "departure") === "both"
                        ? "bg-purple-100 text-purple-800 ring-2 ring-purple-500"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    <Package size={12} />
                    Both
                  </button>
                </div>
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">
                    Radius
                  </label>
                  <span
                    className={`rounded-full px-2 py-1 text-xs font-medium ${
                      (point.type || "departure") === "departure"
                        ? "bg-green-100 text-green-800"
                        : (point.type || "departure") === "delivery"
                          ? "bg-blue-100 text-blue-800"
                          : "bg-purple-100 text-purple-800"
                    }`}
                  >
                    {point.radius} km
                  </span>
                </div>
                <Slider
                  value={[point.radius]}
                  onValueChange={handleRadiusChange}
                  max={500}
                  min={50}
                  step={5}
                  className="w-full"
                />
                <div className="mt-1 flex justify-between text-xs text-gray-400">
                  <span>50km</span>
                  <span>500km</span>
                </div>
              </div>

              <div className="border-t pt-2 text-xs text-gray-500">
                <div>Lat: {point.lat.toFixed(6)}</div>
                <div>Lng: {point.lng.toFixed(6)}</div>
              </div>
            </div>
          </div>
        </Popup>
      </Marker>

      <Circle
        center={[point.lat, point.lng]}
        radius={point.radius * 1000} // Convert km to meters
        pathOptions={{
          ...getCircleColor(),
          fillOpacity: 0.15,
          weight: 2,
          opacity: 0.8,
        }}
      />
    </>
  );
}

// Main component
export function LocationRadiusSelector({
  value = [],
  onChange = () => {},
  defaultCenter = [40.7128, -74.006],
  defaultZoom = 5,
  defaultRadius = 50,
}: LocationRadiusSelectorProps) {
  const [points, setPoints] = useState<Points>(value);
  const [isAddingMode, setIsAddingMode] = useState<boolean>(false);
  const [selectedLocationType, setSelectedLocationType] =
    useState<LocationType>("departure");
  const mapRef = useRef<L.Map>(null);

  // Update parent when points change
  const updatePoints = useCallback(
    (newPoints: Points) => {
      setPoints(newPoints);
      onChange(newPoints);
    },
    [onChange],
  );

  // Add new point
  const handleMapClick = useCallback(
    (latlng: LatLngTuple) => {
      const newPoint: Point = {
        lat: latlng[0],
        lng: latlng[1],
        radius: defaultRadius,
        type: selectedLocationType,
      };

      const newPoints = [...points, newPoint];
      updatePoints(newPoints);
      setIsAddingMode(false);
    },
    [points, defaultRadius, selectedLocationType, updatePoints],
  );

  // Update radius for a specific point
  const handleUpdateRadius = useCallback(
    (index: number, newRadius: number) => {
      const newPoints = points.map((point, i) =>
        i === index ? { ...point, radius: newRadius } : point,
      );
      updatePoints(newPoints);
    },
    [points, updatePoints],
  );

  // Update type for a specific point
  const handleUpdateType = useCallback(
    (index: number, newType: LocationType) => {
      const newPoints = points.map((point, i) =>
        i === index ? { ...point, type: newType } : point,
      );
      updatePoints(newPoints);
    },
    [points, updatePoints],
  );

  // Delete a point
  const handleDeletePoint = useCallback(
    (index: number) => {
      const newPoints = points.filter((_, i) => i !== index);
      updatePoints(newPoints);
    },
    [points, updatePoints],
  );

  // Clear all points
  const handleClearAll = useCallback(() => {
    updatePoints([]);
  }, [updatePoints]);

  // Calculate map center based on points
  const mapCenter: LatLngTuple =
    points.length > 0
      ? [
          points.reduce((sum, p) => sum + p.lat, 0) / points.length,
          points.reduce((sum, p) => sum + p.lng, 0) / points.length,
        ]
      : defaultCenter;

  // Count points by type
  const departureCount = points.filter(
    (p) => (p.type || "departure") === "departure",
  ).length;
  const deliveryCount = points.filter(
    (p) => (p.type || "departure") === "delivery",
  ).length;
  const bothCount = points.filter(
    (p) => (p.type || "departure") === "both",
  ).length;

  return (
    <div className={`w-full space-y-4`}>
      {/* Location Type Selector */}
      {isAddingMode && (
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <label className="mb-3 block text-sm font-medium text-gray-700">
            Select location type to add:
          </label>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setSelectedLocationType("departure")}
              className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                selectedLocationType === "departure"
                  ? "bg-green-100 text-green-800 ring-2 ring-green-500"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              <MapPin size={16} />
              Departure Location
            </button>
            <button
              type="button"
              onClick={() => setSelectedLocationType("delivery")}
              className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                selectedLocationType === "delivery"
                  ? "bg-blue-100 text-blue-800 ring-2 ring-blue-500"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              <Truck size={16} />
              Delivery Location
            </button>
            <button
              type="button"
              onClick={() => setSelectedLocationType("both")}
              className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                selectedLocationType === "both"
                  ? "bg-purple-100 text-purple-800 ring-2 ring-purple-500"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              <Package size={16} />
              Both
            </button>
          </div>
        </div>
      )}

      {/* Map Container with Overlay Controls */}
      <div className="relative overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg">
        <Map
          ref={mapRef}
          center={mapCenter}
          zoom={defaultZoom}
          scrollWheelZoom={true}
          className={clsx(
            isAddingMode ? "cursor-crosshair" : "",
            "h-[500px] w-full",
          )}
        >
          <BasemapLayer />

          <MapClickHandler
            onMapClick={handleMapClick}
            isAddingMode={isAddingMode}
          />

          {points.map((point, index) => (
            <PointMarker
              key={`point-${index}-${point.lat}-${point.lng}`}
              point={point}
              index={index}
              onUpdateRadius={handleUpdateRadius}
              onDelete={handleDeletePoint}
              onTypeChange={handleUpdateType}
            />
          ))}

          <ComponentResize />
        </Map>

        {/* Floating Add Button */}
        <div className="absolute right-4 top-4 z-[1000]">
          <button
            type="button"
            onClick={() => setIsAddingMode(!isAddingMode)}
            className={`group flex h-12 items-center gap-2 rounded-full px-4 font-medium shadow-lg transition-all duration-200 hover:shadow-xl ${
              isAddingMode
                ? "bg-orange-500 text-white hover:bg-orange-600"
                : "border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
            }`}
            title={isAddingMode ? "Cancel adding" : "Add new point"}
          >
            {isAddingMode ? (
              <>
                <Target size={18} className="animate-pulse" />
                <span className="hidden sm:inline">Adding...</span>
              </>
            ) : (
              <>
                <Plus
                  size={18}
                  className="transition-transform group-hover:scale-110"
                />
                <span className="hidden sm:inline">Add Location</span>
              </>
            )}
          </button>
        </div>

        {/* Points Counter */}
        {points.length > 0 && (
          <div className="absolute left-4 top-4 z-[1000] flex items-center gap-2 rounded-full border border-gray-200 bg-white/90 px-3 py-2 text-sm font-medium text-gray-700 shadow-md backdrop-blur-sm">
            <div className="flex items-center gap-3">
              {departureCount > 0 && (
                <div className="flex items-center gap-1">
                  <div className="h-2 w-2 rounded-full bg-green-600"></div>
                  <span>{departureCount} departure</span>
                </div>
              )}
              {deliveryCount > 0 && (
                <div className="flex items-center gap-1">
                  <div className="h-2 w-2 rounded-full bg-blue-600"></div>
                  <span>{deliveryCount} delivery</span>
                </div>
              )}
              {bothCount > 0 && (
                <div className="flex items-center gap-1">
                  <div className="h-2 w-2 rounded-full bg-purple-600"></div>
                  <span>{bothCount} both</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Clear All Button (when points exist) */}
        {points.length > 0 && (
          <div className="absolute bottom-4 right-4 z-[1000]">
            <button
              type="button"
              onClick={handleClearAll}
              className="flex items-center gap-2 rounded-full bg-red-500 px-4 py-2 text-sm font-medium text-white shadow-lg transition-all duration-200 hover:bg-red-600 hover:shadow-xl"
            >
              <Trash2 size={16} />
              <span className="hidden sm:inline">Clear All</span>
            </button>
          </div>
        )}

        {/* Adding mode instruction */}
        {isAddingMode && (
          <div className="absolute bottom-4 left-4 z-[1000] rounded-lg border border-orange-400 bg-orange-500/90 px-4 py-3 text-white shadow-lg backdrop-blur-sm">
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 animate-pulse rounded-full bg-white"></div>
              <span className="text-sm font-medium">
                Click anywhere on the map to add a{" "}
                {selectedLocationType === "both"
                  ? "combined"
                  : selectedLocationType}{" "}
                location
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Points Summary */}
      {points.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-gradient-to-br from-gray-50 to-white p-6 shadow-sm">
          <h3 className="mb-4 text-lg font-semibold text-gray-900">
            Selected Locations
          </h3>

          {/* Departure Locations */}
          {departureCount > 0 && (
            <div className="mb-6">
              <h4 className="mb-3 flex items-center gap-2 text-base font-medium text-green-800">
                <MapPin size={18} />
                Departure Locations ({departureCount})
              </h4>
              <div className="grid gap-3">
                {points
                  .filter(
                    (point) => (point.type || "departure") === "departure",
                  )
                  .map((point, filteredIndex) => {
                    const originalIndex = points.findIndex((p) => p === point);
                    return (
                      <div
                        key={originalIndex}
                        className="group flex items-center justify-between rounded-lg border border-green-200 bg-green-50 px-4 py-3 shadow-sm transition-all hover:border-green-300 hover:shadow-md"
                      >
                        <div className="flex items-center gap-4">
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100 text-sm font-semibold text-green-800">
                            {filteredIndex + 1}
                          </div>
                          <div>
                            <div className="font-medium text-green-900">
                              Departure Point {filteredIndex + 1}
                            </div>
                            <div className="text-sm text-green-600">
                              {point.lat.toFixed(4)}, {point.lng.toFixed(4)}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-800">
                            {point.radius} km
                          </div>
                          <button
                            type="button"
                            onClick={() => handleDeletePoint(originalIndex)}
                            className="rounded-full p-2 text-green-400 opacity-0 transition-all hover:bg-red-50 hover:text-red-600 group-hover:opacity-100"
                            title="Delete point"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          {/* Delivery Locations */}
          {deliveryCount > 0 && (
            <div className="mb-6">
              <h4 className="mb-3 flex items-center gap-2 text-base font-medium text-blue-800">
                <Truck size={18} />
                Delivery Locations ({deliveryCount})
              </h4>
              <div className="grid gap-3">
                {points
                  .filter((point) => (point.type || "departure") === "delivery")
                  .map((point, filteredIndex) => {
                    const originalIndex = points.findIndex((p) => p === point);
                    return (
                      <div
                        key={originalIndex}
                        className="group flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 shadow-sm transition-all hover:border-blue-300 hover:shadow-md"
                      >
                        <div className="flex items-center gap-4">
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-sm font-semibold text-blue-800">
                            {filteredIndex + 1}
                          </div>
                          <div>
                            <div className="font-medium text-blue-900">
                              Delivery Point {filteredIndex + 1}
                            </div>
                            <div className="text-sm text-blue-600">
                              {point.lat.toFixed(4)}, {point.lng.toFixed(4)}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="rounded-full bg-red-100 px-3 py-1 text-sm font-medium text-red-800">
                            {point.radius} km
                          </div>
                          <button
                            type="button"
                            onClick={() => handleDeletePoint(originalIndex)}
                            className="rounded-full p-2 text-red-400 opacity-0 transition-all hover:bg-red-50 hover:text-red-600 group-hover:opacity-100"
                            title="Delete point"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          {/* Both Locations */}
          {bothCount > 0 && (
            <div>
              <h4 className="mb-3 flex items-center gap-2 text-base font-medium text-purple-800">
                <Package size={18} />
                Combined Locations ({bothCount})
              </h4>
              <div className="grid gap-3">
                {points
                  .filter((point) => (point.type || "departure") === "both")
                  .map((point, filteredIndex) => {
                    const originalIndex = points.findIndex((p) => p === point);
                    return (
                      <div
                        key={originalIndex}
                        className="group flex items-center justify-between rounded-lg border border-purple-200 bg-purple-50 px-4 py-3 shadow-sm transition-all hover:border-purple-300 hover:shadow-md"
                      >
                        <div className="flex items-center gap-4">
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-purple-100 text-sm font-semibold text-purple-800">
                            {filteredIndex + 1}
                          </div>
                          <div>
                            <div className="font-medium text-purple-900">
                              Combined Point {filteredIndex + 1}
                            </div>
                            <div className="text-sm text-purple-600">
                              {point.lat.toFixed(4)}, {point.lng.toFixed(4)}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="rounded-full bg-purple-100 px-3 py-1 text-sm font-medium text-purple-800">
                            {point.radius} km
                          </div>
                          <button
                            type="button"
                            onClick={() => handleDeletePoint(originalIndex)}
                            className="rounded-full p-2 text-purple-400 opacity-0 transition-all hover:bg-red-50 hover:text-red-600 group-hover:opacity-100"
                            title="Delete point"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const ComponentResize = () => {
  const map = useMap();

  setTimeout(() => {
    map.invalidateSize();
  }, 0);

  return null;
};

// Example usage component
export function LocationRadiusInput({
  name,
  className,
}: {
  name: string;
  className?: string;
}) {
  const [{}, { value }, { setValue }] = useField(name);

  return (
    <div className={className}>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Location Selector</h1>
        <p className="mt-2 text-gray-600">
          Select departure, delivery, or combined locations with their coverage
          radius
        </p>
      </div>

      <LocationRadiusSelector
        value={value}
        onChange={setValue}
        defaultCenter={[40.7128, -74.006]}
        defaultZoom={3}
        defaultRadius={100}
      />
    </div>
  );
}
